import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Plus, Briefcase, User, Clock, 
  ChevronRight, LayoutGrid, List, MoreHorizontal,
  FileText, MessageSquare, Circle, Table2, ArrowUpDown
} from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';

interface Case {
  id: string;
  topic: string;
  client_name?: string;
  status: string;
  source?: string;
  created_at?: string;
  updated_at?: string;
  intake_summary?: string;
  priority?: string;
  documents_count?: number;
  messages_count?: number;
  deadline?: string;
  category?: string;
  assigned_to?: string;
  progress?: number;
}

interface CasesViewMinimalProps {
  allCases: Case[];
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  statusFilter: string;
  setStatusFilter: (f: string) => void;
  caseFilter: string;
  setCaseFilter: (f: string) => void;
  expandedCase?: string | null;
  setExpandedCase?: (id: string | null) => void;
  navigate: (path: string) => void;
  onCreateCase?: () => void;
}

const CasesViewMinimal: React.FC<CasesViewMinimalProps> = ({
  allCases,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  caseFilter,
  setCaseFilter,
  navigate,
  onCreateCase
}) => {
  const { language } = useTranslation();
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'table'>('list');
  const [hoveredCase, setHoveredCase] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'topic' | 'client_name' | 'status' | 'updated_at'>('updated_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Translations
  const texts = {
    cases: language === 'en' ? 'Cases' : 'Saker',
    newCase: language === 'en' ? 'New case' : 'Ny sak',
    manageCases: language === 'en' ? 'Manage your legal cases' : 'Administrer dine juridiske saker',
    total: language === 'en' ? 'Total' : 'Totalt',
    active: language === 'en' ? 'Active' : 'Aktive',
    new: language === 'en' ? 'New' : 'Nye',
    completed: language === 'en' ? 'Completed' : 'Fullførte',
    searchCases: language === 'en' ? 'Search cases...' : 'Søk i saker...',
    all: language === 'en' ? 'All' : 'Alle',
    reset: language === 'en' ? 'Reset' : 'Nullstill',
    noCases: language === 'en' ? 'No cases' : 'Ingen saker',
    tryAnotherSearch: language === 'en' ? 'Try another search' : 'Prøv et annet søk',
    createFirst: language === 'en' ? 'Create your first case to get started' : 'Opprett din første sak for å komme i gang',
    createCase: language === 'en' ? 'Create case' : 'Opprett sak',
    marketplace: language === 'en' ? 'Marketplace' : 'Markedsplass',
    today: language === 'en' ? 'Today' : 'I dag',
    yesterday: language === 'en' ? 'Yesterday' : 'I går',
    daysAgo: language === 'en' ? 'd ago' : 'd siden',
    caseName: language === 'en' ? 'Case' : 'Sak',
    client: language === 'en' ? 'Client' : 'Klient',
    statusLabel: language === 'en' ? 'Status' : 'Status',
    updated: language === 'en' ? 'Updated' : 'Oppdatert',
    documents: language === 'en' ? 'Docs' : 'Dok',
  };

  // Status map - language aware
  const STATUS_MAP: Record<string, string> = language === 'en' ? {
    NEW: 'New',
    ACTIVE: 'Active',
    ASSIGNED: 'Assigned',
    IN_PROGRESS: 'In Progress',
    WAITING: 'Waiting',
    AWAITING_LAWYER_REVIEW: 'Waiting',
    COMPLETED: 'Completed',
    URGENT: 'Urgent',
  } : {
    NEW: 'Ny',
    ACTIVE: 'Aktiv',
    ASSIGNED: 'Tildelt',
    IN_PROGRESS: 'Pågår',
    WAITING: 'Venter',
    AWAITING_LAWYER_REVIEW: 'Venter',
    COMPLETED: 'Fullført',
    URGENT: 'Haster',
  };

  // Filter and sort cases
  const filteredCases = useMemo(() => {
    let cases = allCases.filter(c => {
      const matchesSearch = !searchQuery || 
        c.topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        c.status?.toUpperCase() === statusFilter.toUpperCase();
      
      const matchesSource = caseFilter === 'all' ||
        (caseFilter === 'marketplace' && c.source === 'MARKETPLACE') ||
        (caseFilter === 'lexy' && c.source !== 'MARKETPLACE');
      
      return matchesSearch && matchesStatus && matchesSource;
    });
    
    // Sort
    cases.sort((a, b) => {
      let aVal: string | number = a[sortField] || '';
      let bVal: string | number = b[sortField] || '';
      
      if (sortField === 'updated_at') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return cases;
  }, [allCases, searchQuery, statusFilter, caseFilter, sortField, sortDirection]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Stats
  const stats = useMemo(() => ({
    total: allCases.length,
    active: allCases.filter(c => c.status !== 'COMPLETED').length,
    new: allCases.filter(c => c.status === 'NEW').length,
    completed: allCases.filter(c => c.status === 'COMPLETED').length,
  }), [allCases]);

  // Format relative time
  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return texts.today;
    if (diffDays === 1) return texts.yesterday;
    if (diffDays < 7) return `${diffDays}${texts.daysAgo}`;
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'nb-NO', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="h-full bg-white overflow-y-auto overflow-x-hidden">
      {/* Centered content container with max-width */}
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        
        {/* Header - ultra minimal */}
        <div className="mb-10 sm:mb-14">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">{texts.cases}</h1>
              <p className="text-gray-400 text-sm mt-2 hidden sm:block">{texts.manageCases}</p>
            </div>
            <button
              onClick={() => onCreateCase ? onCreateCase() : navigate('/lexy/case/new')}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-all hover:shadow-lg w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              {texts.newCase}
            </button>
          </div>
        </div>

        {/* Stats row - Responsive grid */}
        <div className="grid grid-cols-4 gap-4 sm:gap-8 mb-10 sm:mb-14">
          <div className="text-center">
            <div className="text-2xl sm:text-4xl font-semibold text-gray-900">{stats.total}</div>
            <div className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mt-2">{texts.total}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-4xl font-semibold text-gray-900">{stats.active}</div>
            <div className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mt-2">{texts.active}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-4xl font-semibold text-gray-900">{stats.new}</div>
            <div className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mt-2">{texts.new}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-4xl font-semibold text-gray-900">{stats.completed}</div>
            <div className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider mt-2">{texts.completed}</div>
          </div>
        </div>
        
        {/* Search and filters - responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6 pb-5 border-b border-gray-100">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <input
              type="text"
              placeholder={texts.searchCases}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-6 pr-4 py-2 bg-transparent border-0 text-sm placeholder:text-gray-300 focus:outline-none transition-all"
            />
          </div>
          
          {/* Filter tabs - scrollable on mobile */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {[
              { id: 'all', label: texts.all },
              { id: 'NEW', label: texts.new },
              { id: 'ACTIVE', label: texts.active },
              { id: 'COMPLETED', label: texts.completed },
            ].map(status => (
              <button
                key={status.id}
                onClick={() => setStatusFilter(status.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all whitespace-nowrap ${
                  statusFilter === status.id
                    ? 'text-gray-900'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div className="hidden sm:flex items-center gap-1">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-all ${
                viewMode === 'list' ? 'text-gray-900' : 'text-gray-300 hover:text-gray-500'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded transition-all ${
                viewMode === 'table' ? 'text-gray-900' : 'text-gray-300 hover:text-gray-500'
              }`}
              title="Table view"
            >
              <Table2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-all ${
                viewMode === 'grid' ? 'text-gray-900' : 'text-gray-300 hover:text-gray-500'
              }`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results count - subtle */}
        <div className="mb-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">
            {filteredCases.length} {texts.cases.toLowerCase()}
            {(searchQuery || statusFilter !== 'all') && (
              <button 
                onClick={() => { setSearchQuery(''); setStatusFilter('all'); }}
                className="ml-3 text-gray-500 hover:text-gray-700 normal-case tracking-normal"
              >
                {texts.reset}
              </button>
            )}
          </p>
        </div>
        
        {/* Cases list/grid */}
        <AnimatePresence mode="wait">
          {filteredCases.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-16 sm:py-24 text-center"
            >
              <Briefcase className="w-8 h-8 text-gray-200 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">{texts.noCases}</h3>
              <p className="text-gray-400 text-sm mb-8">
                {searchQuery ? texts.tryAnotherSearch : texts.createFirst}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => onCreateCase ? onCreateCase() : navigate('/lexy/case/new')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-all hover:shadow-lg"
                >
                  <Plus className="w-4 h-4" />
                  {texts.createCase}
                </button>
              )}
            </motion.div>
          ) : viewMode === 'list' ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-0"
            >
              {filteredCases.map((caseItem, index) => (
                <motion.div
                  key={caseItem.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  onMouseEnter={() => setHoveredCase(caseItem.id)}
                  onMouseLeave={() => setHoveredCase(null)}
                  onClick={() => navigate(`/lexy/case/${caseItem.id}/workspace`)}
                  className="group flex items-center gap-3 sm:gap-4 py-3 cursor-pointer transition-all hover:bg-gray-50/50 rounded-lg px-2 -mx-2"
                >
                  {/* Status indicator - colored based on status */}
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    caseItem.status === 'ACTIVE' || caseItem.status === 'IN_PROGRESS' 
                      ? 'bg-emerald-400' 
                      : caseItem.status === 'NEW' 
                        ? 'bg-blue-400'
                        : caseItem.status === 'COMPLETED'
                          ? 'bg-gray-300'
                          : 'bg-gray-300'
                  }`} />
                  
                  {/* Title & client */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <h3 className="text-sm sm:text-[15px] font-medium text-gray-900 group-hover:text-gray-700 truncate">{caseItem.topic}</h3>
                      {caseItem.source === 'MARKETPLACE' && (
                        <span className="text-[10px] font-medium text-indigo-500 uppercase tracking-wide hidden sm:inline">
                          {texts.marketplace}
                        </span>
                      )}
                    </div>
                    {caseItem.client_name && (
                      <p className="text-xs sm:text-sm text-gray-400 truncate">{caseItem.client_name}</p>
                    )}
                  </div>
                  
                  {/* Meta - subtle, hidden on mobile */}
                  <div className="hidden sm:flex items-center gap-8 text-sm text-gray-400">
                    <span className="w-20">{STATUS_MAP[caseItem.status] || caseItem.status}</span>
                    <span className="w-16 text-right">{formatTime(caseItem.updated_at || caseItem.created_at)}</span>
                  </div>
                  
                  {/* Status badge on mobile */}
                  <span className={`sm:hidden text-[10px] px-1.5 py-0.5 rounded ${
                    caseItem.status === 'ACTIVE' || caseItem.status === 'IN_PROGRESS' 
                      ? 'bg-emerald-100 text-emerald-600'
                      : caseItem.status === 'NEW'
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-500'
                  }`}>
                    {STATUS_MAP[caseItem.status] || caseItem.status}
                  </span>
                  
                  {/* Arrow - appears on hover */}
                  <ChevronRight className={`w-4 h-4 text-gray-300 transition-all opacity-0 group-hover:opacity-100 hidden sm:block ${
                    hoveredCase === caseItem.id ? 'translate-x-0.5' : ''
                  }`} />
                </motion.div>
              ))}
            </motion.div>
          ) : viewMode === 'table' ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0"
            >
              {/* Minimalist table header - clean grid layout */}
              <div className="grid grid-cols-[1fr_160px_100px_100px_24px] gap-4 px-2 pb-3 mb-2 border-b border-gray-100 items-center min-w-[600px]">
                <button
                  onClick={() => handleSort('topic')}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors text-left"
                >
                  {texts.caseName}
                  <ArrowUpDown className="w-3 h-3 opacity-50" />
                </button>
                <button
                  onClick={() => handleSort('client_name')}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors text-left"
                >
                  {texts.client}
                  <ArrowUpDown className="w-3 h-3 opacity-50" />
                </button>
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors text-left"
                >
                  {texts.statusLabel}
                  <ArrowUpDown className="w-3 h-3 opacity-50" />
                </button>
                <button
                  onClick={() => handleSort('updated_at')}
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors text-left"
                >
                  {texts.updated}
                  <ArrowUpDown className="w-3 h-3 opacity-50" />
                </button>
                <div /> {/* Spacer for arrow */}
              </div>
              
              {/* Minimalist table rows - clean grid layout */}
              <div className="space-y-0 min-w-[600px]">
                {filteredCases.map((caseItem, index) => (
                  <motion.div
                    key={caseItem.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => navigate(`/lexy/case/${caseItem.id}/workspace`)}
                    className="group grid grid-cols-[1fr_160px_100px_100px_24px] gap-4 px-2 py-3 cursor-pointer hover:bg-gray-50/50 rounded-lg transition-all items-center"
                  >
                    {/* Case name with subtle indicator */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        caseItem.status === 'ACTIVE' || caseItem.status === 'IN_PROGRESS' 
                          ? 'bg-emerald-400' 
                          : caseItem.status === 'NEW' 
                            ? 'bg-blue-400'
                            : caseItem.status === 'COMPLETED'
                              ? 'bg-gray-300'
                              : 'bg-gray-300'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <span className="text-[15px] font-medium text-gray-900 group-hover:text-gray-700 truncate block">
                          {caseItem.topic}
                        </span>
                        {caseItem.source === 'MARKETPLACE' && (
                          <span className="text-[10px] font-medium text-indigo-500 uppercase tracking-wide">
                            {texts.marketplace}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Client */}
                    <div className="text-sm text-gray-500 truncate">
                      {caseItem.client_name || '—'}
                    </div>
                    
                    {/* Status - subtle */}
                    <div className="text-sm text-gray-400">
                      {STATUS_MAP[caseItem.status] || caseItem.status}
                    </div>
                    
                    {/* Updated - subtle */}
                    <div className="text-sm text-gray-400">
                      {formatTime(caseItem.updated_at || caseItem.created_at)}
                    </div>
                    
                    {/* Subtle arrow on hover */}
                    <ChevronRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3"
            >
              {filteredCases.map((caseItem, index) => (
                <motion.div
                  key={caseItem.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  whileHover={{ y: -2 }}
                  onClick={() => navigate(`/lexy/case/${caseItem.id}/workspace`)}
                  className="group p-4 sm:p-5 bg-white hover:bg-gray-50/50 rounded-xl cursor-pointer transition-all"
                >
                  {/* Status dot and time */}
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        caseItem.status === 'ACTIVE' || caseItem.status === 'IN_PROGRESS' 
                          ? 'bg-emerald-400' 
                          : caseItem.status === 'NEW' 
                            ? 'bg-blue-400'
                            : 'bg-gray-300'
                      }`} />
                      <span className="text-xs text-gray-400">
                        {STATUS_MAP[caseItem.status] || caseItem.status}
                      </span>
                    </div>
                    <span className="text-xs text-gray-300">
                      {formatTime(caseItem.updated_at || caseItem.created_at)}
                    </span>
                  </div>
                  
                  <h3 className="text-[15px] font-medium text-gray-900 mb-2 line-clamp-2 group-hover:text-gray-700 transition-colors">
                    {caseItem.topic}
                  </h3>
                  
                  {caseItem.client_name && (
                    <p className="text-sm text-gray-400">{caseItem.client_name}</p>
                  )}
                  
                  {caseItem.source === 'MARKETPLACE' && (
                    <span className="inline-block mt-3 text-[10px] font-medium text-indigo-500 uppercase tracking-wide">
                      {texts.marketplace}
                    </span>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CasesViewMinimal;
